import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ReactComponent as CalendarSVG } from '@assets/SVGs/QontactPoint/CalendarSVG.svg';
import { ReactComponent as ArrowSVG } from '@assets/SVGs/QontactPoint/ArrowSVG.svg';
import { ReactComponent as WarningSVG } from '@assets/SVGs/QontactPoint/WarningSVG.svg';
import TimeInput from '@components/General/TimeInput';
import { DayPickerPopper } from '@components/General/TablePoppers';
import { ShiftRecipe } from '@models/QontactPoint';
import turnoStyles from '@styles/components/QontactPoints/Turnos/QontactPointTurnos.module.css';
import styles from '@styles/screens/ColaboradorDedicatedShiftsScreen.module.css';

interface UserTurnoValue extends ShiftRecipe {
}

interface UserTurnoProps {
    turno: UserTurnoValue;
    onChange: (turno: UserTurnoValue) => void;
    isEditable: boolean;
    isItemFromList?: boolean;
    errorMessage?: string;
}

const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

const UserTurno: React.FC<UserTurnoProps> = ({
    turno,
    onChange,
    isEditable,
    isItemFromList = true,
    errorMessage,
}) => {
    const selectedDays = useMemo(() => {
        if (
            turno.type === 'weekly' &&
            turno.configurationData &&
            turno.configurationData.weekdays
        ) {
            return Object.values(turno.configurationData.weekdays).map((dayIndex: any) => dayNames[dayIndex]);
        }
        return [];
    }, [turno.configurationData, turno.type]);

    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const daysBarRef = useRef<HTMLDivElement>(null);
    const anchorElement = useRef<HTMLDivElement>(null);
    const [popperWidth, setPopperWidth] = useState(0);

    useEffect(() => {
        const container = containerRef.current;
        const onWheel = (event) => {
            if (event.deltaY !== 0) {
                event.preventDefault();
                if (container) {
                    container.scrollLeft += event.deltaY;
                }
            }
        };

        if (container) {
            container.addEventListener('wheel', onWheel);
        }

        const updatePopperWidth = () => {
            if (daysBarRef.current) {
                setPopperWidth(daysBarRef.current.offsetWidth);
            }
        };

        updatePopperWidth();
        window.addEventListener('resize', updatePopperWidth);

        return () => {
            if (container) {
                container.removeEventListener('wheel', onWheel);
            }
            window.removeEventListener('resize', updatePopperWidth);
        };
    }, []);

    const updateTurno = (patch: Partial<UserTurnoValue>) => {
        onChange({
            ...turno,
            ...patch,
            expected: 1,
        });
    };

    const handleDaysChange = (days: string[]) => {
        updateTurno({
            type: days.length === 0 || days.length === 7 ? 'daily' : 'weekly',
            configurationData: {
                weekdays: days.map((day) => dayNames.indexOf(day)).sort(),
            },
        });
    };

    const handleDayRemove = (day: string) => {
        handleDaysChange(selectedDays.filter((selectedDay) => selectedDay !== day));
    };

    const handleClickDayDropdown = () => {
        setAnchorEl(anchorElement.current);
        setIsOpen((previousOpen) => !previousOpen);
    };

    return (
        <div
            className={turnoStyles['turno-container']}
            style={
                isItemFromList
                    ? { marginBottom: '0px', paddingLeft: '0px', height: '100%', minHeight: '100%' }
                    : { marginBottom: '0px', paddingLeft: '0px', height: '100%', minHeight: '100%' }
            }
        >
            <div className={styles['user-turno-column']}>
                <div className={styles['name-row']}>
                    <div className={styles['name-field']}>
                        <div className={turnoStyles['input-container']}>
                            <input
                                type="text"
                                placeholder="Nombre turno..."
                                className={turnoStyles['input']}
                                value={turno.name}
                                onChange={(e) => updateTurno({ name: e.target.value })}
                                disabled={!isEditable}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles['details-row']}>
                    <div className={styles['time-panel']}>
                        <div className={turnoStyles['hours-container']} style={{ gap: '16px', justifyContent: 'flex-start' }}>
                            <div className={turnoStyles['hour-container']} style={{ width: '132px', minWidth: '132px', maxWidth: '132px' }}>
                                <p>Desde</p>
                                <TimeInput
                                    key={`from-${turno.hourFrom}`}
                                    initValue={turno.hourFrom === '00:00' ? '' : turno.hourFrom}
                                    setHour={(hour) => updateTurno({ hourFrom: hour })}
                                    isEnabled={isEditable}
                                />
                            </div>

                            <div className={turnoStyles['hour-container']} style={{ width: '132px', minWidth: '132px', maxWidth: '132px' }}>
                                <p>Hasta</p>
                                <TimeInput
                                    key={`to-${turno.hourTo}`}
                                    initValue={turno.hourTo === '00:00' ? '' : turno.hourTo}
                                    setHour={(hour) => updateTurno({ hourTo: hour })}
                                    isEnabled={isEditable}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles['days-group']}>
                        <p>Día/s que aplica</p>
                        <div className={turnoStyles['days-bar']} ref={daysBarRef}>
                            <div className={turnoStyles['svg-container']}>
                                <CalendarSVG />
                            </div>

                            <div
                                className={turnoStyles['days-window']}
                                ref={containerRef}
                                onClick={() => {
                                    if (isEditable) {
                                        setAnchorEl(anchorElement.current);
                                        setIsOpen(true);
                                    }
                                }}
                                style={{ cursor: isEditable ? 'pointer' : 'default' }}
                            >
                                {selectedDays.length === 0 && (
                                    <p style={{ marginBottom: '0px', fontSize: '14px' }}>
                                        {isEditable ? 'Todos los dias' : turno.type === 'daily' ? 'Todos los dias' : 'Sin dias seleccionados'}
                                    </p>
                                )}
                                {selectedDays.map((day) => (
                                    <div key={day} className={turnoStyles['day-pill']}>
                                        {day}
                                        {isEditable && (
                                            <span
                                                className={turnoStyles['remove-day']}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDayRemove(day);
                                                }}
                                            >
                                                &times;
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {isEditable && (
                                <div
                                    className={turnoStyles['svg-container']}
                                    style={{
                                        transform: isOpen ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 0.2s ease',
                                        cursor: 'pointer',
                                    }}
                                    onClick={handleClickDayDropdown}
                                    ref={anchorElement}
                                >
                                    <ArrowSVG />
                                    {isOpen && (
                                        <DayPickerPopper
                                            isOpen={isOpen}
                                            setIsOpen={setIsOpen}
                                            anchorElement={anchorEl}
                                            placement="bottom-end"
                                            selectedDays={selectedDays}
                                            setSelectedDays={handleDaysChange}
                                            style={{
                                                marginTop: '12px',
                                                marginRight: '-15px',
                                                width: popperWidth,
                                                zIndex: 1301,
                                            }}
                                            clickAwayHandler={() => {}}
                                            showSelectAll={false}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {errorMessage && (
                    <div className={styles['error-container']} role="alert">
                        <div className={styles['error-icon']}>
                            <WarningSVG />
                        </div>
                        <div className={styles['error-content']}>
                            <span className={styles['error-title']}>Revisar turno</span>
                            <span className={styles['error-message']}>{errorMessage}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserTurno;
